import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingUp } from 'lucide-react';

const InvestmentCalculatorTool = () => {
  const [initialAmount, setInitialAmount] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [years, setYears] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const initial = parseFloat(initialAmount) || 0;
    const monthly = parseFloat(monthlyContribution) || 0;
    const rate = parseFloat(interestRate);
    const t = parseFloat(years);

    if (isNaN(rate) || isNaN(t) || t <= 0 || (initial <= 0 && monthly <= 0)) {
      setResult(null);
      return;
    }

    const monthlyRate = rate / 100 / 12;
    const totalMonths = t * 12;

    // Future Value of initial investment: P * (1 + r)^n
    let fvInitial;
    if (monthlyRate === 0) {
      fvInitial = initial;
    } else {
      fvInitial = initial * Math.pow(1 + monthlyRate, totalMonths);
    }

    // Future Value of monthly contributions (ordinary annuity)
    // FV = PMT * [((1 + r)^n - 1) / r]
    let fvContributions;
    if (monthlyRate === 0) {
      fvContributions = monthly * totalMonths;
    } else {
      fvContributions = monthly * ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
    }

    const finalValue = fvInitial + fvContributions;
    const totalContributions = initial + (monthly * totalMonths);
    const totalProfit = finalValue - totalContributions;
    const profitPercent = totalContributions > 0 ? (totalProfit / totalContributions) * 100 : 0;

    setResult({
      initial,
      monthly,
      rate,
      years: t,
      finalValue,
      totalContributions,
      totalProfit,
      profitPercent,
      fvInitial,
      fvContributions
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInitialAmount('');
    setMonthlyContribution('');
    setInterestRate('');
    setYears('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Initial Amount ($)
            </label>
            <input
              type="number"
              value={initialAmount}
              onChange={(e) => setInitialAmount(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="initial-amount-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Monthly Contribution ($)
            </label>
            <input
              type="number"
              value={monthlyContribution}
              onChange={(e) => setMonthlyContribution(e.target.value)}
              placeholder="e.g., 500"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="monthly-contribution-input"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g., 8"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="interest-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Years
            </label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              placeholder="e.g., 20"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="years-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-investment-btn"
      >
        <Calculator size={18} />
        Calculate Investment
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="investment-results">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Final Value</span>
                <button
                  onClick={() => copyResult(result.finalValue)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-final-value"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-green-400" data-testid="final-value-result">
                {formatCurrency(result.finalValue)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Contributions</span>
                <button
                  onClick={() => copyResult(result.totalContributions)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-contributions"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-blue-400" data-testid="contributions-result">
                {formatCurrency(result.totalContributions)}
              </div>
            </div>

            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Total Profit</span>
                <button
                  onClick={() => copyResult(result.totalProfit)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-profit"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-yellow-400" data-testid="profit-result">
                {formatCurrency(result.totalProfit)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                +{result.profitPercent.toFixed(1)}% return
              </div>
            </div>
          </div>

          {/* Growth Visualization */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-green-400" />
              <span className="text-sm font-medium text-gray-300">Investment Breakdown</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-gray-400">Contributions</div>
                <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded"
                    style={{ width: `${(result.totalContributions / result.finalValue) * 100}%` }}
                  />
                </div>
                <div className="w-16 text-xs text-right text-gray-400">
                  {((result.totalContributions / result.finalValue) * 100).toFixed(0)}%
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs text-gray-400">Profit</div>
                <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded"
                    style={{ width: `${(result.totalProfit / result.finalValue) * 100}%` }}
                  />
                </div>
                <div className="w-16 text-xs text-right text-gray-400">
                  {((result.totalProfit / result.finalValue) * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Investment Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Initial Investment</span>
                <span className="text-white font-medium">{formatCurrency(result.initial)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly Contribution</span>
                <span className="text-white font-medium">{formatCurrency(result.monthly)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Annual Interest Rate</span>
                <span className="text-white font-medium">{result.rate}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Investment Period</span>
                <span className="text-white font-medium">{result.years} years</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Growth from Initial</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.fvInitial - result.initial)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Growth from Contributions</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.fvContributions - (result.monthly * result.years * 12))}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Historical Average Returns</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">S&P 500</div>
            <div>~10% annually</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Bonds</div>
            <div>~4-6% annually</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">High-Yield Savings</div>
            <div>~4-5% annually</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Investment Tips</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Start early - time is your biggest advantage</div>
          <div>• Consistent contributions build wealth faster</div>
          <div>• Diversify to manage risk</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-investment-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default InvestmentCalculatorTool;
